"use client";

import Image from "next/image";
import { useEffect, useState } from "react";

type Props = {
  /** Pool of image srcs to cycle through. */
  images: string[];
  /** Which image to start on (and stagger tiles so they don't sync). */
  start?: number;
  /** Time each image stays visible, in ms. */
  interval?: number;
  /**
   * Controlled active index. When provided, the component shows this image and
   * skips its internal timer — a parent decides what each instance displays.
   */
  active?: number;
  className?: string;
};

/**
 * Crossfades through a list of images. All images are stacked and toggled via
 * opacity so the swap is a smooth fade. Either self-cycles on a timer, or — when
 * `active` is given — is driven by a parent (so several instances can be kept in
 * sync without ever showing the same image). Honours prefers-reduced-motion.
 */
export default function CyclingImage({
  images,
  start = 0,
  interval = 4000,
  active,
  className,
}: Props) {
  const controlled = active !== undefined;
  const [self, setSelf] = useState(images.length ? start % images.length : 0);
  const index = controlled ? active! : self;

  useEffect(() => {
    if (controlled) return;
    if (images.length < 2) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    const id = setInterval(
      () => setSelf((i) => (i + 1) % images.length),
      interval
    );
    return () => clearInterval(id);
  }, [controlled, images.length, interval]);

  return (
    <>
      {images.map((src, i) => (
        <Image
          key={src}
          src={src.startsWith("/") ? src : `/${src}`}
          alt=""
          fill
          sizes="(min-width: 1024px) 260px, 50vw"
          className={`object-cover transition-opacity duration-1000 ${
            i === index ? "opacity-100" : "opacity-0"
          } ${className ?? ""}`}
        />
      ))}
    </>
  );
}
